import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import UDText from 'ud-ui/components/text';

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  min-height: 72px;
  padding: 0 20px;
  border-radius: ${({ theme }) => theme.radii.card};
  background-color: ${({ theme }) => theme.colors.SurfaceCard};

  &.clickable {
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;

    &:hover:not(:has(button:hover, a:hover, input:hover, [role='button']:hover)) {
      background-color: ${({ theme }) => theme.colors.AccentCornsilk};
    }
  }

  /*
   * Выбранная строка обязана отличаться от строки под курсором: ховер — это «сюда
   * можно нажать», выбор — «уже нажали». Поэтому у выбора своя, более насыщенная
   * заливка и полоса слева, а ховер поверх выбора лишь затемняет её на шаг.
   */
  &.selected {
    background-color: ${({ theme }) => theme.colors.CasePendingSurfaceActive};
    /* inset-полоса — акцент выделения, а не уровень подъёма: токены elevation здесь неприменимы. */
    box-shadow: inset 3px 0 0 ${({ theme }) => theme.colors.AccentNaplesYellow};
  }

  &.clickable.selected:hover:not(:has(button:hover, a:hover, input:hover, [role='button']:hover)) {
    background-color: ${({ theme }) => theme.colors.DarkYellow200};
  }
`;

const Avatar = styled.div<{ size: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: ${({ theme }) => theme.radii.card};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DEFAULT_AVATAR_SIZE = 60;

export interface UDEntityRowProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'onClick'> {
  /** Порядковый номер. Без него колонка номера не рисуется. */
  index?: number | string;
  /** Строка — это адрес картинки; узел рисуется как есть (иконка, инициалы). */
  avatar?: string | React.ReactNode;
  avatarSize?: number;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Правый блок: кнопки, меню, что угодно. Рисует страница. */
  actions?: React.ReactNode;
  /** Строка выбрана: своя заливка и полоса слева, отдельно от состояния под курсором. */
  isSelected?: boolean;
  onClick?: () => void;
}

/**
 * Готовая строка списка: номер, аватар, заголовок с подзаголовком, действия.
 *
 * Пресет, а не обязательная часть `UDPagedList`: страница вправе нарисовать
 * строку сама через `renderItem`. Написан с нуля и не наследует `UDListItem` —
 * тот жёстко задаёт три колонки по 33% и тянет за собой вёрстку старой сборки
 * категорий.
 */
const UDEntityRow = ({
  index,
  avatar,
  avatarSize = DEFAULT_AVATAR_SIZE,
  title,
  subtitle,
  actions,
  isSelected,
  onClick,
  className,
  ...rest
}: UDEntityRowProps) => {
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // Клик по кнопке внутри строки — это действие кнопки, а не переход по строке.
      const isInteractive = (event.target as HTMLElement).closest('button, a, input, select, [role="button"]');
      if (!isInteractive) onClick?.();
    },
    [onClick],
  );

  return (
    <div role="listitem">
      <Row
        {...rest}
        className={classNames(className, { clickable: Boolean(onClick), selected: Boolean(isSelected) })}
        onClick={handleClick}
      >
        {index !== undefined && (
          <UDText type="subhead" className="w-10 shrink-0 text-center font-medium">
            {index}
          </UDText>
        )}

        {avatar !== undefined && avatar !== null && (
          <Avatar size={avatarSize}>{typeof avatar === 'string' ? <img src={avatar} alt="" /> : avatar}</Avatar>
        )}

        <div className="min-w-0 flex-1">
          <UDText type="subhead" className="truncate font-medium">
            {title}
          </UDText>
          {subtitle && (
            <UDText type="footnote" className="text-surfaceRomanSilver30 truncate">
              {subtitle}
            </UDText>
          )}
        </div>

        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </Row>
    </div>
  );
};

export default UDEntityRow;
